import { useState, useEffect } from 'react';
import api from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Pill, Printer, ChevronDown } from 'lucide-react';
import { openPrintWindow } from '@/lib/letterhead';

const PRIORITA = {
  alta: { badge: 'bg-red-100 text-red-700 border-red-200', bordo: 'border-red-200 bg-red-50/40', label: 'Priorita alta' },
  media: { badge: 'bg-amber-100 text-amber-700 border-amber-200', bordo: 'border-amber-200 bg-amber-50/40', label: 'Priorita media' },
};

export default function RevisioneFarmaciCard({ ospiteId }) {
  const [dati, setDati] = useState(null);
  const [aperto, setAperto] = useState(null);

  useEffect(() => {
    api.get(`/alert/revisione-farmaci/${ospiteId}`).then(r => setDati(r.data)).catch(() => {});
  }, [ospiteId]);

  const stampa = () => {
    const righe = dati.farmaci.map(f => `
      <tr>
        <td><b>${f.farmaco}</b>${f.dosaggio ? `<br><span style="font-size:11px">${f.dosaggio}</span>` : ''}</td>
        <td>${f.classe}</td>
        <td>${f.priorita === 'alta' ? 'ALTA' : 'Media'}</td>
        <td style="font-size:11px">${f.motivo}</td>
        <td style="font-size:11px">${f.azione}</td>
      </tr>`).join('');
    openPrintWindow('Revisione farmacologica', `
      <h2>Revisione farmacologica - rischio di caduta</h2>
      <p><b>Ospite:</b> ${dati.ospite_nome} &nbsp;&nbsp; <b>Terapie attive:</b> ${dati.n_terapie_attive}
      ${dati.conley ? ` &nbsp;&nbsp; <b>Conley:</b> ${dati.conley.punteggio} (${dati.conley.classe})` : ''}
      ${dati.barthel ? ` &nbsp;&nbsp; <b>Barthel:</b> ${dati.barthel.punteggio}` : ''}</p>
      <p>${dati.sintesi}</p>
      ${dati.polifarmacia ? `<p><b>${dati.nota_polifarmacia}</b></p>` : ''}
      <table style="width:100%;border-collapse:collapse" border="1" cellpadding="5">
        <thead><tr><th align="left">Farmaco</th><th>Classe</th><th>Priorita</th><th align="left">Motivo del rischio</th><th align="left">Azione suggerita</th></tr></thead>
        <tbody>${righe}</tbody>
      </table>
      <p style="font-size:11px;margin-top:16px">Indicazioni derivate dai criteri STOPP/START e Beers. La decisione
      terapeutica resta di competenza del medico curante.</p>
      <p style="margin-top:28px">Firma del medico ____________________________</p>
    `);
  };

  if (!dati) return null;

  return (
    <Card data-testid="revisione-farmaci-card">
      <CardHeader className="pb-2">
        <CardTitle className="text-base font-semibold text-slate-800 flex items-center gap-2 flex-wrap">
          <Pill className="w-4 h-4 text-brand" />Revisione farmaci a rischio caduta
          {dati.rischio_caduta_elevato && <Badge className="text-[10px] bg-red-100 text-red-700">rischio caduta elevato</Badge>}
          {dati.polifarmacia && <Badge className="text-[10px] bg-amber-100 text-amber-700" data-testid="badge-polifarmacia">polifarmacia {dati.n_terapie_attive} farmaci</Badge>}
          {dati.farmaci.length > 0 && (
            <Button size="sm" variant="outline" className="h-7 text-xs gap-1 ml-auto" onClick={stampa} data-testid="stampa-revisione-btn">
              <Printer className="w-3 h-3" />Stampa per il medico
            </Button>
          )}
        </CardTitle>
        <p className="text-xs text-slate-500">{dati.sintesi}</p>
      </CardHeader>
      <CardContent className="space-y-2">
        {dati.polifarmacia && <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-2.5">{dati.nota_polifarmacia}</p>}
        {dati.farmaci.length === 0 && <p className="text-xs text-emerald-700">Nessun farmaco a rischio caduta in terapia attiva.</p>}
        {dati.farmaci.map((f, i) => {
          const s = PRIORITA[f.priorita];
          const open = aperto === i;
          return (
            <div key={i} className={`rounded-lg border ${s.bordo}`} data-testid={`farmaco-rischio-${i}`}>
              <button className="w-full flex items-center justify-between gap-3 p-3 text-left" onClick={() => setAperto(open ? null : i)}>
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-slate-800 truncate">{f.farmaco}</p>
                  <p className="text-[11px] text-slate-500">{f.classe}{f.dosaggio ? ` · ${f.dosaggio}` : ''}{f.al_bisogno ? ' · al bisogno' : ''}</p>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <Badge className={`text-[10px] border ${s.badge}`}>{s.label}</Badge>
                  <ChevronDown className={`w-4 h-4 text-slate-400 transition-transform ${open ? 'rotate-180' : ''}`} />
                </div>
              </button>
              {open && (
                <div className="px-3 pb-3 space-y-2">
                  <div>
                    <p className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider">Motivo del rischio</p>
                    <p className="text-xs text-slate-700">{f.motivo}</p>
                  </div>
                  <div>
                    <p className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider">Azione / alternativa suggerita</p>
                    <p className="text-xs text-slate-700">{f.azione}</p>
                  </div>
                </div>
              )}
            </div>
          );
        })}
        {dati.farmaci.length > 0 && (
          <p className="text-[10px] text-slate-400 italic">
            Indicazioni derivate dai criteri STOPP/START e Beers. La decisione terapeutica resta del medico curante.
          </p>
        )}
      </CardContent>
    </Card>
  );
}
